import type { CompanyLocationDto, DeliveryLocationDto } from '@api/customers'

import {
  MolsonAutocomplete,
  MolsonBox,
  MolsonButton,
  MolsonDialog,
  MolsonDialogActions,
  MolsonDialogContent,
  MolsonDialogHeader,
  MolsonInputLabel,
  MolsonTextField,
  MolsonTypography
} from '@components/reusable'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputLabelWrapper } from '../styled-components/InputLabelWrapper'

type AddressType = 'delivery' | 'billing'

export type DeleteAddressDialogProps = {
  /** The type of address being deleted */
  addressType: AddressType
  /** Whether the dialog is open */
  isOpen: boolean
  /** Callback to close the dialog */
  onClose: () => void
  /** The address to be deleted */
  address: DeliveryLocationDto | CompanyLocationDto
  /** Available addresses to transfer quotes/orders to */
  availableAddresses: (DeliveryLocationDto | CompanyLocationDto)[]
  /** Whether the address being deleted is the default */
  isDefaultAddress: boolean
  /** The customer name for the confirmation message */
  customerName: string
  /** Whether the delete operation is pending */
  isPending?: boolean
  /**
   * Callback when delete is confirmed
   * @param params.transferToAddressId - ID of the address to transfer quotes/orders to
   * @param params.newDefaultAddressId - ID of the new default address (only if deleting default)
   */
  onDelete: (params: {
    transferToAddressId: number
    newDefaultAddressId?: number
  }) => Promise<void>
}

type FormValues = {
  transferToAddress: DeliveryLocationDto | CompanyLocationDto | null
  newDefaultAddress: DeliveryLocationDto | CompanyLocationDto | null
}

export const DeleteAddressDialog = ({
  addressType,
  isOpen,
  onClose,
  address,
  availableAddresses,
  isDefaultAddress,
  customerName,
  isPending = false,
  onDelete
}: DeleteAddressDialogProps) => {
  const { t } = useTranslation()

  const {
    control,
    formState: { errors },
    getValues,
    trigger
  } = useForm<FormValues>({
    defaultValues: {
      transferToAddress: availableAddresses[0] ?? null,
      newDefaultAddress: availableAddresses[0] ?? null
    }
  })

  const getAddressLabel = (
    location: string | DeliveryLocationDto | CompanyLocationDto
  ): string => {
    if (typeof location === 'string') {
      return location
    }

    // Build the full address label
    const parts = [
      location.addressName,
      location.address1,
      location.address2,
      location.address3,
      location.state,
      location.city,
      location.zipCode,
      location.country
    ].filter(Boolean)

    return parts.join(', ')
  }

  const handleDeleteAddress = async () => {
    const transferToLocationId = getValues('transferToAddress')?.id
    const newDefaultLocationId = getValues('newDefaultAddress')?.id

    if (!transferToLocationId) {
      return
    }

    await onDelete({
      transferToAddressId: Number(transferToLocationId),
      newDefaultAddressId: isDefaultAddress
        ? Number(newDefaultLocationId)
        : undefined
    })

    onClose()
  }

  const isDeliveryAddress = addressType === 'delivery'
  const isDeleteDisabled =
    !getValues('transferToAddress') ||
    (isDefaultAddress && !getValues('newDefaultAddress')) ||
    isPending

  // Build the address display for the address being deleted
  const addressLines = [
    address.addressName,
    address.address1,
    address.address2,
    address.address3,
    address.state,
    address.city,
    address.zipCode,
    address.country
  ].filter(Boolean)

  // Get translation keys based on address type
  const dialogTitle = t(
    isDeliveryAddress
      ? 'common.deleteDeliveryAddress'
      : 'common.deleteBillingAddress'
  )
  const subtitle = t(
    `deleteAddress.${addressType}Subtitle` as 'deleteAddress.deliverySubtitle'
  )
  const addressLabel = t(
    isDeliveryAddress ? 'common.deliveryAddress' : 'common.billingAddress'
  )
  const selectNewDefaultText = t(
    `deleteAddress.selectNewDefault${isDeliveryAddress ? 'Delivery' : 'Billing'}Text` as 'deleteAddress.selectNewDefaultDeliveryText',
    { customerName }
  )
  const defaultAddressLabel = t(
    isDeliveryAddress
      ? 'common.defaultDeliveryAddress'
      : 'common.defaultBillingAddress'
  )

  return (
    <MolsonDialog
      open={isOpen}
      fullWidth={true}
      maxWidth="md"
      onClose={onClose}
      disableScrollLock={true}
    >
      <MolsonDialogHeader
        title={dialogTitle}
        hasCloseIcon={true}
        handleCloseDialog={onClose}
        displayDivider={true}
      />
      <MolsonDialogContent>
        <MolsonBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <MolsonTypography>{subtitle}</MolsonTypography>

          {/* Display the address being deleted */}
          <MolsonBox>
            {addressLines.map((line, index) => (
              <MolsonTypography
                key={`line-${line?.length ?? 0}-${index}`}
                fontWeight={index === 0 ? 'bold' : 'normal'}
              >
                {line}
              </MolsonTypography>
            ))}
          </MolsonBox>

          {/* Dropdown to select transfer location */}
          <InputLabelWrapper>
            <MolsonInputLabel htmlFor="transferToAddress" required={true}>
              {addressLabel}
            </MolsonInputLabel>
            <Controller
              name="transferToAddress"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('common.fieldMandatory', {
                    field: addressLabel
                  })
                }
              }}
              render={({ field: { onChange, value, name } }) => (
                <MolsonAutocomplete
                  value={value}
                  onChange={(_, newValue) => {
                    onChange(newValue)
                    trigger('transferToAddress')
                  }}
                  options={availableAddresses}
                  getOptionLabel={getAddressLabel}
                  renderInput={params => (
                    <MolsonTextField
                      {...params}
                      name={name}
                      error={!!errors.transferToAddress}
                      helperText={errors.transferToAddress?.message}
                    />
                  )}
                />
              )}
            />
          </InputLabelWrapper>

          {/* Show default address selector if deleting the default */}
          {isDefaultAddress ? (
            <>
              <MolsonTypography>{selectNewDefaultText}</MolsonTypography>
              <InputLabelWrapper>
                <MolsonInputLabel htmlFor="newDefaultAddress" required={true}>
                  {defaultAddressLabel}
                </MolsonInputLabel>
                <Controller
                  name="newDefaultAddress"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t('common.fieldMandatory', {
                        field: defaultAddressLabel
                      })
                    }
                  }}
                  render={({ field: { onChange, value, name } }) => (
                    <MolsonAutocomplete
                      value={value}
                      onChange={(_, newValue) => {
                        onChange(newValue)
                        trigger('newDefaultAddress')
                      }}
                      options={availableAddresses}
                      getOptionLabel={getAddressLabel}
                      renderInput={params => (
                        <MolsonTextField
                          {...params}
                          name={name}
                          error={!!errors.newDefaultAddress}
                          helperText={errors.newDefaultAddress?.message}
                        />
                      )}
                    />
                  )}
                />
              </InputLabelWrapper>
            </>
          ) : null}
        </MolsonBox>
      </MolsonDialogContent>
      <MolsonDialogActions>
        <MolsonButton variant="outlined" color="secondary" onClick={onClose}>
          {t('common.cancel')}
        </MolsonButton>
        <MolsonButton
          variant="contained"
          color="error"
          onClick={handleDeleteAddress}
          loading={isPending}
          disabled={isDeleteDisabled}
        >
          {t('common.delete')}
        </MolsonButton>
      </MolsonDialogActions>
    </MolsonDialog>
  )
}
